import * as Cesium from "cesium";

export const addGrid = (viewer, gridSize, gridColor) => {
  // Function to get the visible geographical bounds of the current camera view
  function getVisibleBounds() {
    const scene = viewer.scene;

    // Check if the scene is in 2D mode
    if (scene.mode === Cesium.SceneMode.SCENE2D) {
      const canvas = scene.canvas;

      // Pick the coordinates of the top-left and bottom-right corners of the viewport
      const topLeft = viewer.camera.pickEllipsoid(new Cesium.Cartesian2(0, 0), scene.globe.ellipsoid);
      const bottomRight = viewer.camera.pickEllipsoid(new Cesium.Cartesian2(canvas.width, canvas.height), scene.globe.ellipsoid);

      // Return null if the coordinates are not found
      if (!topLeft || !bottomRight) {
        return null;
      }

      // Convert Cartesian coordinates to Cartographic (latitude/longitude)
      const topLeftCarto = Cesium.Ellipsoid.WGS84.cartesianToCartographic(topLeft);
      const bottomRightCarto = Cesium.Ellipsoid.WGS84.cartesianToCartographic(bottomRight);

      // Return the geographical bounds in degrees
      return {
        west: Cesium.Math.toDegrees(topLeftCarto.longitude),
        north: Cesium.Math.toDegrees(topLeftCarto.latitude),
        east: Cesium.Math.toDegrees(bottomRightCarto.longitude),
        south: Cesium.Math.toDegrees(bottomRightCarto.latitude),
      };
    } else {
      // If the scene is in 3D mode, compute the view rectangle
      const rectangle = viewer.camera.computeViewRectangle(scene.globe.ellipsoid);
      if (!rectangle) {
        return null;
      }

      // Return the geographical bounds in degrees
      return {
        west: Cesium.Math.toDegrees(rectangle.west),
        south: Cesium.Math.toDegrees(rectangle.south),
        east: Cesium.Math.toDegrees(rectangle.east),
        north: Cesium.Math.toDegrees(rectangle.north),
      };
    }
  }

  // Function to get the number of meters per degree of latitude (constant value)
  function metersPerDegreeLatitude() {
    return 111320; // Average distance in meters per degree of latitude
  }

  // Function to get the number of meters per degree of longitude at a given latitude
  function metersPerDegreeLongitude(latitude) {
    return 111320 * Math.cos(Cesium.Math.toRadians(latitude)); // Adjusted by the cosine of the latitude
  }

  // Check if the viewer is valid
  if (viewer) {
    // Get the current visible bounds of the map
    const bounds = getVisibleBounds(viewer);

    if (!bounds) {
      return;
    }
    // Calculate the midpoint latitude
    const midLat = (bounds.north + bounds.south) / 2;

    // Calculate the number of meters per degree for both latitude and longitude
    const latMeters = metersPerDegreeLatitude();
    const lonMeters = metersPerDegreeLongitude(midLat);

    // Calculate the width and height of the visible area in meters
    const widthInMeters = (bounds.east - bounds.west) * lonMeters;
    const heightInMeters = (bounds.north - bounds.south) * latMeters;

    // Calculate the number of grid lines for X and Y directions
    const linesX = widthInMeters / gridSize;
    const linesY = heightInMeters / gridSize;

    // Add the grid to the viewer as a rectangle with grid material
    const EPS = 1e-8; // tiny epsilon in degrees
    let { west, south, east, north } = bounds;

    // --- Normalize bounds ---
    if (east < west) [west, east] = [east, west]; // swap if reversed
    if (north < south) [south, north] = [north, south]; // swap if reversed
    if (east - west < EPS) east = west + EPS; // ensure east > west
    if (north - south < EPS) north = south + EPS; // ensure north > south

    viewer.entities.add({
      rectangle: {
        coordinates: Cesium.Rectangle.fromDegrees(west, south, east, north),
        material: new Cesium.GridMaterialProperty({
          color: Cesium.Color.fromCssColorString(gridColor).withAlpha(0.5),
          cellAlpha: 0.0,
          lineCount: new Cesium.Cartesian2(linesX, linesY),
          lineThickness: new Cesium.Cartesian2(1.5, 1.5),
        }),
        height: 0,
      },
    });
  }
};

export const removeGrid = (viewer) => {
  // Check if the viewer is valid
  if (viewer) {
    // Filter all entities to find those that have a rectangle with GridMaterialProperty
    viewer.entities.values
      .filter((entity) => entity.rectangle && entity.rectangle.material instanceof Cesium.GridMaterialProperty)
      // Remove each grid entity from the viewer
      .forEach((gridEntity) => viewer.entities.remove(gridEntity));
  }
};
